/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useRecents } from './hooks/useRecents'
import { isMenuItemActive, convertToMenuItems, SidebarMenuItem } from './utils'
import { Menu, Divider } from 'antd'
import { NavLink, Link } from 'react-router-dom'
import { observer } from 'mobx-react-lite'

import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { ROUTES } from '@sportninja/common/constants/app'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'

import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { useMobxStore } from 'src/state'

import Text from 'src/components/ts-components/Text'
import { useOrganizationsTree } from './hooks/useOrganizationsTree'
import OrganizationsTree from './OrganizationsTree'
import HomeIcon from '../icons/HomeIcon'
import MyScheduleIcon from '../icons/MyScheduleIcon'
import MyMembershipIcon from '../icons/MyMembershipIcon'
import MyRegistrationsIcon from '../icons/MyRegistrationsIcon'
import CompetitionsIcon from '../icons/CompetitionsIcon'
import TeamsIcon from '../icons/TeamsIcon'
import MyTeamsIcon from '../icons/MyTeamsIcon'
import OrganizationsIcon from '../icons/OrganizationsIcon'
import RecentsIcon from '../icons/RecentsIcon'
import DemoLeaguesIcon from '../icons/DemoLeaguesIcon'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import SortableSidebarItem, { SidebarItemContent } from './SortableSidebarItem'
import { saveSidebarOrder, loadSidebarOrder } from './utils/localStorage'
import {
  dividerStyle,
  sectionTitleTransitionStyle,
  recentsGroupLabelStyle,
  itemLinkStyle,
  itemImageContainerStyle,
  itemImageStyle,
  itemTextStyle,
  sidebarMenuContainerStyle,
  menuStyle,
} from './styles'
import TeamsTree from './TeamsTree'
import useTeamsTree from './hooks/useTeamsTree'

interface SidebarMenuProps {
  collapsed: boolean
  currentPath: string
  onExpand?: () => void
  isMobile: boolean
}

const getImage = (item: any, fallback: string) => {
  if (item?.image?.formats && item?.image?.formats?.length > 0) {
    return getImageThumbnailId(item)
  }
  return getImageThumbnailId(item) || item?.imageId || fallback
}

// Home item (top of menu)
const homeItem: SidebarMenuItem = {
  key: 'home',
  title: 'Home',
  to: ROUTES.HOME_ROOT,
  icon: <HomeIcon size={22} />,
}

// My Dashboard section items
const myDashboardItems: SidebarMenuItem[] = [
  {
    key: 'my-schedule',
    title: 'My Schedule',
    to: ROUTES.MY_SCHEDULE,
    icon: <MyScheduleIcon size={22} />,
  },
  {
    key: 'my-registrations',
    title: 'My Registrations',
    to: ROUTES.REGISTRATIONS.ROOT,
    icon: <MyRegistrationsIcon size={22} />,
    subKey: 'registration',
    notInclude: ['schedule'],
  },
  {
    key: 'teams',
    title: 'My Teams',
    to: ROUTES.TEAMS.ROOT,
    icon: <MyTeamsIcon size={22} />,
    subKey: 'teams',
    notInclude: ['schedule'],
  },
  {
    key: 'my-membership',
    title: 'My Membership',
    to: ROUTES.MY_MEMBERSHIP,
    icon: <MyMembershipIcon size={22} />,
  },
]

// Operations section items
const operationsItems: SidebarMenuItem[] = [
  {
    key: 'organizations',
    title: isCanlan ? 'Leagues' : 'Organizations',
    to: ROUTES.ORGS.ROOT,
    icon: <OrganizationsIcon size={22} />,
    subKey: 'org',
    notInclude: ['following', 'my-membership'],
  },
  {
    key: 'competitions',
    title: isCanlan ? 'Seasons' : 'Competitions',
    to: ROUTES.SCHEDULES.ROOT,
    icon: <CompetitionsIcon size={22} />,
    subKey: 'schedule',
    notInclude: ['following', 'my-schedule', 'my-membership'],
  },
  {
    key: 'teams',
    title: 'Teams',
    to: ROUTES.TEAMS.ROOT,
    icon: <TeamsIcon size={22} />,
    subKey: 'team',
    notInclude: ['following', 'my-membership'],
  },
]

// Utilities section items
const utilitiesItems: SidebarMenuItem[] = [
  {
    key: 'recents',
    title: 'Recents',
    to: ROUTES.COMING_SOON,
    icon: <RecentsIcon size={22} />,
  },
  {
    key: 'demo-leagues',
    title: 'Demo Leagues',
    to: ROUTES.COMING_SOON,
    icon: <DemoLeaguesIcon size={22} />,
  },
]

const demoLeaguesData = [
  {
    id: 'soccer-sandbox-leagues',
    fullName: 'Soccer League',
  },
  {
    id: 'hockey-sandbox-leagues',
    fullName: 'Hockey League',
  },
]

/**
 * SidebarMenu component - renders the navigation menu items with sections
 */
const SidebarMenu: React.FC<SidebarMenuProps> = observer(
  ({ collapsed, currentPath, onExpand, isMobile }) => {
    const {
      me: {
        me,
        state: { account },
      },
    } = useMobxStore()

    // State for controlled openKeys in Operations and Utilities menus
    const [operationsOpenKeys, setOperationsOpenKeys] = useState<string[]>([
      'organizations',
    ])
    const [utilitiesOpenKeys, setUtilitiesOpenKeys] = useState<string[]>([])
    const [myDashboardOpenKeys, setMyDashboardOpenKeys] = useState<string[]>([
      'teams',
    ])
    const [pendingOpenKey, setPendingOpenKey] = useState<string | null>(null)

    // Handle auto-opening menu after sidebar expands
    useEffect(() => {
      if (!collapsed && pendingOpenKey) {
        // Determine which menu section the key belongs to
        if (pendingOpenKey === 'organizations') {
          setOperationsOpenKeys((prev) =>
            prev.includes(pendingOpenKey) ? prev : [...prev, pendingOpenKey]
          )
        } else if (pendingOpenKey === 'teams') {
          setMyDashboardOpenKeys((prev) =>
            prev.includes(pendingOpenKey) ? prev : [...prev, pendingOpenKey]
          )
        } else if (
          pendingOpenKey === 'recents' ||
          pendingOpenKey === 'demo-leagues'
        ) {
          setUtilitiesOpenKeys((prev) =>
            prev.includes(pendingOpenKey) ? prev : [...prev, pendingOpenKey]
          )
        }
        setPendingOpenKey(null)
      }
    }, [collapsed, pendingOpenKey])

    // Auto-expand Organizations submenu when navigating to an org URL
    useEffect(() => {
      // Only expand if sidebar is not collapsed
      if (collapsed) {
        return
      }

      // Check if current path is an org page
      const isOrgPage = currentPath.match(/^\/org\//)
      if (isOrgPage) {
        setOperationsOpenKeys((prev) =>
          prev.includes('organizations') ? prev : [...prev, 'organizations']
        )
      }
    }, [currentPath, collapsed])

    const {
      data: organizationsTreeData,
      isLoading: isLoadingOrgsTree,
      error: orgsTreeError,
    } = useOrganizationsTree()

    const {
      data: teamsTreeData,
      isLoading: isLoadingTeamsTree,
      error: teamsTreeError,
    } = useTeamsTree()

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    )

    // State for item order
    const [myDashboardOrder, setMyDashboardOrder] = React.useState<string[]>(
      () => {
        const saved = loadSidebarOrder(String(me?.id || ''), 'myDashboard')
        return saved || myDashboardItems.map((item) => item.key)
      }
    )

    const [operationsOrder, setOperationsOrder] = React.useState<string[]>(
      () => {
        const saved = loadSidebarOrder(String(me?.id || ''), 'operations')
        return saved || operationsItems.map((item) => item.key)
      }
    )

    const [utilitiesOrder, setUtilitiesOrder] = React.useState<string[]>(() => {
      const saved = loadSidebarOrder(String(me?.id || ''), 'utilities')
      return saved || utilitiesItems.map((item) => item.key)
    })

    // Reload sidebar order when user ID becomes available (after login)
    useEffect(() => {
      if (me?.id) {
        const userId = String(me.id)

        const savedMyDashboard = loadSidebarOrder(userId, 'myDashboard')
        if (savedMyDashboard) {
          setMyDashboardOrder(savedMyDashboard)
        }

        const savedOperations = loadSidebarOrder(userId, 'operations')
        if (savedOperations) {
          setOperationsOrder(savedOperations)
        }

        const savedUtilities = loadSidebarOrder(userId, 'utilities')
        if (savedUtilities) {
          setUtilitiesOrder(savedUtilities)
        }
      }
    }, [me?.id])

    // Track which item is being dragged for DragOverlay
    const [activeId, setActiveId] = React.useState<string | null>(null)
    const [activeSection, setActiveSection] = React.useState<
      'myDashboard' | 'operations' | 'utilities' | null
    >(null)

    const handleDragStart = (
      event: DragStartEvent,
      section: 'myDashboard' | 'operations' | 'utilities'
    ) => {
      setActiveId(event.active.id as string)
      setActiveSection(section)
    }

    const handleDragEnd = (
      event: DragEndEvent,
      section: 'myDashboard' | 'operations' | 'utilities'
    ) => {
      const { active, over } = event

      if (over && active.id !== over.id) {
        let setOrder: React.Dispatch<React.SetStateAction<string[]>>
        let currentOrder: string[]

        switch (section) {
          case 'myDashboard':
            setOrder = setMyDashboardOrder
            currentOrder = myDashboardOrder
            break
          case 'operations':
            setOrder = setOperationsOrder
            currentOrder = operationsOrder
            break
          case 'utilities':
            setOrder = setUtilitiesOrder
            currentOrder = utilitiesOrder
            break
        }

        const oldIndex = currentOrder.indexOf(active.id as string)
        const newIndex = currentOrder.indexOf(over.id as string)
        const newOrder = arrayMove(currentOrder, oldIndex, newIndex)

        setOrder(newOrder)
        saveSidebarOrder(String(me?.id || ''), section, newOrder)
      }

      setActiveId(null)
      setActiveSection(null)
    }

    // Find the active item for DragOverlay
    const getActiveItem = useCallback(() => {
      if (!activeId || !activeSection) {
        return null
      }

      let items: SidebarMenuItem[]
      switch (activeSection) {
        case 'myDashboard':
          items = myDashboardItems
          break
        case 'operations':
          items = operationsItems
          break
        case 'utilities':
          items = utilitiesItems
          break
        default:
          return null
      }

      return items.find((item) => item.key === activeId)
    }, [activeId, activeSection])

    const activeItem = getActiveItem()

    const recents = useRecents(me?.id)

    const handleSubmenuClick = useCallback(
      (key: string) => {
        if (collapsed && onExpand) {
          onExpand()
          setPendingOpenKey(key)
        }
      },
      [collapsed, onExpand]
    )

    const getRecentsMenuItem = useCallback(() => {
      const entityTypes = [
        {
          type: ENTITY_TYPES.org,
          label: isCanlan ? 'Leagues' : 'Organizations',
          baseRoute: ROUTES.ORG_ROOT,
          fallback: '/images/generic_placeholders/placeholder_organization.png',
        },
        {
          type: ENTITY_TYPES.schedule,
          label: isCanlan ? 'Seasons' : 'Competitions',
          baseRoute: ROUTES.SCHEDULE_ROOT,
          fallback: '/images/generic_placeholders/placeholder_competition.png',
        },
        {
          type: ENTITY_TYPES.team,
          label: 'Teams',
          baseRoute: ROUTES.TEAM_ROOT,
          fallback: '/images/generic_placeholders/placeholder_team.png',
        },
      ]

      // Check if there are any recents before rendering anything
      const hasAnyRecents = entityTypes.some(({ type }) => {
        const items = recents[type] || []
        if (type === ENTITY_TYPES.org) {
          return (
            items.filter(
              (item: any) =>
                item.id !== account?.organization?.url?.split('/').pop()
            ).length > 0
          )
        }
        return items.length > 0
      })

      if (!hasAnyRecents) {
        return null
      }

      // When collapsed, show simple item - Ant Design Menu handles tooltip natively
      if (collapsed) {
        return {
          key: 'recents',
          icon: (
            <span
              onClick={() => handleSubmenuClick('recents')}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <RecentsIcon size={22} />
            </span>
          ),
          label: 'Recents',
          onClick: () => handleSubmenuClick('recents'),
        }
      }

      const children: any[] = []

      entityTypes.forEach(({ type, label, baseRoute, fallback }) => {
        const recentItems = recents[type] || []

        // Filter out current organization for org type
        const filteredItems =
          type === ENTITY_TYPES.org
            ? recentItems.filter(
                (item) =>
                  item.id !== account?.organization?.url?.split('/').pop()
              )
            : recentItems

        if (filteredItems.length > 0) {
          // Add group label
          children.push({
            key: `recents-group-${type}`,
            type: 'group',
            label: (
              <div css={recentsGroupLabelStyle}>
                <Text variant='paragraphSmall' color={colors.NEUTRAL_100}>
                  Recent {label}
                </Text>
              </div>
            ),
          })

          // Add items for this entity type
          filteredItems.forEach((item: any) => {
            const name = item?.name || item?.name_full || item?.fullName
            const nameLength = name.length
            let firstLine = name
            let secondLine = ''

            if (nameLength > 25) {
              const words = name.split(' ')
              let currentLine = ''
              for (const word of words) {
                if ((currentLine + ' ' + word).length <= 25) {
                  currentLine += (currentLine ? ' ' : '') + word
                } else {
                  break
                }
              }
              firstLine = currentLine
              secondLine = name.slice(currentLine.length)
            }

            children.push({
              key: `recent-${type}-${item.id}`,
              label: (
                <Link to={`${baseRoute}/${item.id}`} css={itemLinkStyle}>
                  <div css={itemImageContainerStyle}>
                    <img
                      loading='lazy'
                      src={getImage(item, fallback)}
                      alt={name}
                      width={32}
                      height={32}
                      css={itemImageStyle}
                      onError={(e: any) => {
                        e.target.onerror = null
                        e.target.src = fallback
                      }}
                    />
                  </div>
                  <p css={itemTextStyle}>
                    {firstLine}
                    {secondLine && <br />}
                    {secondLine}
                  </p>
                </Link>
              ),
              type: 'item',
            })
          })
        }
      })

      if (children.length === 0) {
        return null
      }

      return {
        key: 'recents',
        icon: <RecentsIcon size={22} />,
        label: 'Recents',
        children,
      }
    }, [account?.organization?.url, recents, collapsed, handleSubmenuClick])

    const getDemoLeaguesMenuItem = useCallback(() => {
      // Only show if not Canlan and user has account_id
      if (isCanlan || !me?.account_id) {
        return null
      }

      // When collapsed, show simple item - Ant Design Menu handles tooltip natively
      if (collapsed) {
        return {
          key: 'demo-leagues',
          icon: (
            <span
              onClick={() => handleSubmenuClick('demo-leagues')}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <DemoLeaguesIcon size={22} />
            </span>
          ),
          label: 'Demo Leagues',
          onClick: () => handleSubmenuClick('demo-leagues'),
        }
      }

      const children = demoLeaguesData.map((item: any) => {
        const name = item?.fullName
        const nameLength = name.length
        let firstLine = name
        let secondLine = ''

        if (nameLength > 25) {
          const words = name.split(' ')
          let currentLine = ''
          for (const word of words) {
            if ((currentLine + ' ' + word).length <= 25) {
              currentLine += (currentLine ? ' ' : '') + word
            } else {
              break
            }
          }
          firstLine = currentLine
          secondLine = name.slice(currentLine.length)
        }

        return {
          key: `demo-league-${item.id}`,
          label: (
            <Link to={`${ROUTES.SANDBOX}/${item.id}`} css={itemLinkStyle}>
              <div css={itemImageContainerStyle}>
                <img
                  loading='lazy'
                  src={getImage(
                    item,
                    '/images/generic_placeholders/placeholder_competition.png'
                  )}
                  alt={name}
                  width={32}
                  height={32}
                  css={itemImageStyle}
                  onError={(e: any) => {
                    e.target.onerror = null
                    e.target.src =
                      '/images/generic_placeholders/placeholder_competition.png'
                  }}
                />
              </div>
              <p css={itemTextStyle}>
                {firstLine}
                {secondLine && <br />}
                {secondLine}
              </p>
            </Link>
          ),
          type: 'item',
        }
      })

      return {
        key: 'demo-leagues',
        icon: <DemoLeaguesIcon />,
        label: 'Demo Leagues',
        children,
      }
    }, [me?.account_id, collapsed, handleSubmenuClick])

    const getOrganizationsMenuItem = useCallback(() => {
      // Hide if there's an error OR if data is loaded but empty
      if (
        orgsTreeError ||
        (!isLoadingOrgsTree &&
          (!organizationsTreeData || organizationsTreeData.length === 0))
      ) {
        return null
      }

      const label = isCanlan ? 'Leagues' : 'Organizations'

      // When collapsed, show simple item - Ant Design Menu handles tooltip natively
      if (collapsed) {
        return {
          key: 'organizations',
          icon: (
            <span
              onClick={() => handleSubmenuClick('organizations')}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <OrganizationsIcon size={22} />
            </span>
          ),
          label,
          onClick: () => handleSubmenuClick('organizations'),
        }
      }

      // If loading or no data yet, show loading state
      if (isLoadingOrgsTree || !organizationsTreeData) {
        return {
          key: 'organizations',
          icon: <OrganizationsIcon size={22} />,
          label,
          children: [
            {
              key: 'organizations-loading',
              label: (
                <div css={itemLinkStyle}>
                  <Text variant='paragraphSmall' color={colors.NEUTRAL_100}>
                    Loading...
                  </Text>
                </div>
              ),
              type: 'item',
            },
          ],
        }
      }

      return {
        key: 'organizations',
        icon: <OrganizationsIcon size={22} />,
        label,
        children: [
          {
            key: 'organizations-tree',
            label: <OrganizationsTree data={organizationsTreeData} />,
            type: 'item',
          },
        ],
      }
    }, [
      organizationsTreeData,
      isLoadingOrgsTree,
      orgsTreeError,
      collapsed,
      handleSubmenuClick,
    ])

    const getTeamsMenuItem = useCallback(() => {
      // Hide if there's an error OR if data is loaded but empty
      if (
        teamsTreeError ||
        (!isLoadingTeamsTree && (!teamsTreeData || teamsTreeData.length === 0))
      ) {
        return null
      }

      const label = 'My Teams'

      // When collapsed, show simple item - Ant Design Menu handles tooltip natively
      if (collapsed) {
        return {
          key: 'teams',
          icon: (
            <span
              onClick={() => handleSubmenuClick('teams')}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <MyTeamsIcon size={22} />
            </span>
          ),
          label,
          onClick: () => handleSubmenuClick('teams'),
        }
      }

      // If loading or no data yet, show loading state
      if (isLoadingTeamsTree || !teamsTreeData) {
        return {
          key: 'teams',
          icon: <MyTeamsIcon size={22} />,
          label,
          children: [
            {
              key: 'teams-loading',
              label: (
                <div css={itemLinkStyle}>
                  <Text variant='paragraphSmall' color={colors.NEUTRAL_100}>
                    Loading...
                  </Text>
                </div>
              ),
              type: 'item',
            },
          ],
        }
      }

      return {
        key: 'teams',
        icon: <MyTeamsIcon size={22} />,
        label,
        children: [
          {
            key: 'teams-tree',
            label: <TeamsTree data={teamsTreeData} />,
            type: 'item',
          },
        ],
      }
    }, [
      teamsTreeError,
      collapsed,
      isLoadingTeamsTree,
      teamsTreeData,
      handleSubmenuClick,
    ])

    const homeMenuItems = useMemo(
      () => [
        {
          key: homeItem.key,
          icon: homeItem.icon,
          label: <NavLink to={homeItem.to}>{homeItem.title}</NavLink>,
        },
      ],
      []
    )

    // Filter out My Teams on mobile
    const filteredMyDashboardItems = isMobile
      ? myDashboardItems.filter((item) => item.key !== 'teams')
      : myDashboardItems

    const myDashboardMenuItems = convertToMenuItems(
      filteredMyDashboardItems,
      getRecentsMenuItem,
      getDemoLeaguesMenuItem,
      undefined,
      getTeamsMenuItem
    )
      .sort((a, b) => {
        return (
          myDashboardOrder.indexOf(a!.key as string) -
          myDashboardOrder.indexOf(b!.key as string)
        )
      })
      .map((item) => ({
        ...item,
        icon: null,
        label: (
          <SortableSidebarItem
            id={item!.key as string}
            icon={item!.icon}
            disabled={collapsed}
          >
            {item!.label}
          </SortableSidebarItem>
        ),
      }))

    // Filter Operations items based on platform and data availability
    const filteredOperationsItems = useMemo(() => {
      let items = operationsItems

      // Hide Organizations on mobile
      if (isMobile) {
        items = items.filter((item) => item.key !== 'organizations')
      }

      return items
    }, [isMobile])

    const operationsMenuItems = convertToMenuItems(
      filteredOperationsItems,
      getRecentsMenuItem,
      getDemoLeaguesMenuItem,
      getOrganizationsMenuItem
    )
      .sort((a, b) => {
        return (
          operationsOrder.indexOf(a!.key as string) -
          operationsOrder.indexOf(b!.key as string)
        )
      })
      .map((item) => ({
        ...item,
        icon: null,
        label: (
          <SortableSidebarItem
            id={item!.key as string}
            icon={item!.icon}
            disabled={collapsed}
          >
            {item!.label}
          </SortableSidebarItem>
        ),
      }))

    const utilitiesMenuItems = convertToMenuItems(
      utilitiesItems,
      getRecentsMenuItem,
      getDemoLeaguesMenuItem
    )
      .sort((a, b) => {
        return (
          utilitiesOrder.indexOf(a!.key as string) -
          utilitiesOrder.indexOf(b!.key as string)
        )
      })
      .map((item) => ({
        ...item,
        icon: null,
        label: (
          <SortableSidebarItem
            id={item!.key as string}
            icon={item!.icon}
            disabled
          >
            {item!.label}
          </SortableSidebarItem>
        ),
      }))

    return (
      <div css={sidebarMenuContainerStyle(collapsed)}>
        {/* Home */}
        <Menu
          mode='inline'
          selectedKeys={
            isMenuItemActive(
              currentPath,
              homeItem.to,
              homeItem.key,
              undefined,
              []
            )
              ? [homeItem.key]
              : []
          }
          style={menuStyle}
          items={homeMenuItems}
        />

        <Divider css={dividerStyle} />

        {/* My Dashboard Section */}
        <div css={sectionTitleTransitionStyle(collapsed)}>My Dashboard</div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(e) => handleDragStart(e, 'myDashboard')}
          onDragEnd={(e) => handleDragEnd(e, 'myDashboard')}
        >
          <SortableContext
            items={myDashboardOrder}
            strategy={verticalListSortingStrategy}
          >
            <Menu
              mode='inline'
              openKeys={collapsed ? [] : myDashboardOpenKeys}
              onOpenChange={(keys) => setMyDashboardOpenKeys(keys as string[])}
              selectedKeys={myDashboardItems
                .filter((item) =>
                  isMenuItemActive(
                    currentPath,
                    item.to,
                    item.key,
                    item.subKey,
                    item?.notInclude || []
                  )
                )
                .map((item) => item.key)}
              style={menuStyle}
              items={myDashboardMenuItems}
            />
          </SortableContext>
          <DragOverlay>
            {activeItem && activeSection === 'myDashboard' ? (
              <SidebarItemContent icon={activeItem.icon} isDragOverlay>
                {activeItem.title}
              </SidebarItemContent>
            ) : null}
          </DragOverlay>
        </DndContext>

        <>
          <Divider css={dividerStyle} />

          {/* Operations Section */}
          <div css={sectionTitleTransitionStyle(collapsed)}>Operations</div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e) => handleDragStart(e, 'operations')}
            onDragEnd={(e) => handleDragEnd(e, 'operations')}
          >
            <SortableContext
              items={operationsOrder}
              strategy={verticalListSortingStrategy}
            >
              <Menu
                mode='inline'
                openKeys={collapsed ? [] : operationsOpenKeys}
                onOpenChange={(keys) => setOperationsOpenKeys(keys as string[])}
                selectedKeys={operationsItems
                  .filter((item) =>
                    isMenuItemActive(
                      currentPath,
                      item.to,
                      item.key,
                      item.subKey,
                      item?.notInclude || []
                    )
                  )
                  .map((item) => item.key)}
                style={menuStyle}
                items={operationsMenuItems}
              />
            </SortableContext>
            <DragOverlay>
              {activeItem && activeSection === 'operations' ? (
                <SidebarItemContent icon={activeItem.icon} isDragOverlay>
                  {activeItem.title}
                </SidebarItemContent>
              ) : null}
            </DragOverlay>
          </DndContext>

          {utilitiesMenuItems.length > 0 && !isMobile && (
            <>
              <Divider css={dividerStyle} />

              {/* Utilities Section */}
              <div css={sectionTitleTransitionStyle(collapsed)}>Utilities</div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(e) => handleDragStart(e, 'utilities')}
                onDragEnd={(e) => handleDragEnd(e, 'utilities')}
              >
                <SortableContext
                  items={utilitiesOrder}
                  strategy={verticalListSortingStrategy}
                >
                  <Menu
                    mode='inline'
                    openKeys={collapsed ? [] : utilitiesOpenKeys}
                    onOpenChange={(keys) =>
                      setUtilitiesOpenKeys(keys as string[])
                    }
                    selectedKeys={utilitiesItems
                      .filter((item) =>
                        isMenuItemActive(
                          currentPath,
                          item.to,
                          item.key,
                          item.subKey,
                          item?.notInclude || []
                        )
                      )
                      .map((item) => item.key)}
                    style={menuStyle}
                    items={utilitiesMenuItems}
                  />
                </SortableContext>
                <DragOverlay>
                  {activeItem && activeSection === 'utilities' ? (
                    <SidebarItemContent icon={activeItem.icon} isDragOverlay>
                      {activeItem.title}
                    </SidebarItemContent>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </>
          )}
        </>
      </div>
    )
  }
)

export default SidebarMenu
